import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CustomError } from 'pricing-pages-lib/errors/CustomErrors';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { useSelector } from 'react-redux';
import { PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { SCREENS } from '../../constants/tracking/Screens';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAppSelector } from '../../reducers';
import { daysSinceProductStartDateFromProductInventorySelector, highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { trackFeatureTableInteraction } from '../../utils/tracking/trackingEventUtils';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { useErrorBoundary } from '../error-boundary/useErrorBoundary';
import withErrorBoundary from '../error-boundary/withErrorBoundary';
import { PinnedFeaturesConfig } from './PinnedFeaturesConfig';
import ProductFeatureTableHeaderCell from './product-feature-table/ProductFeatureTableHeaderCell';
import { usePinnedFeaturesByProductLine } from '../../hooks/useFeatures';
import { isUngatedForInbound25Selector } from '../../selectors/GatingSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const buildFeatureTableHeaderData = ({
  filteredHighlightedProducts,
  showRecommended,
  recommendedProductLevel
}) => {
  return filteredHighlightedProducts.reduce((headerDataMap, apiName) => {
    return Object.assign({}, headerDataMap, {
      [apiName]: {
        Content: /*#__PURE__*/_jsx(ProductFeatureTableHeaderCell, {
          apiName: apiName,
          showRecommended: showRecommended,
          recommendedProductLevel: recommendedProductLevel
        })
      }
    });
  }, {});
};
const buildFeatureTableBodyData = ({
  features,
  productLine,
  highlightedProducts,
  screen,
  staticDisplayRowCount
}) => {
  return {
    sharedData: {
      subscreen: screen || SCREENS[productLine],
      staticDisplayRowCount
    },
    sectionData: {
      [productLine]: {
        apiNames: highlightedProducts,
        features: features || [],
        LabelContent: /*#__PURE__*/_jsx(_Fragment, {})
      }
    }
  };
};
const ProductFeatureTable = ({
  productLine,
  highlightedProducts,
  screen,
  shouldShowRecommended = false,
  recommendedProductLevel = PROFESSIONAL
}) => {
  var _PinnedFeaturesConfig;
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const isUngatedForInbound25 = useAppSelector(isUngatedForInbound25Selector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    featuresByProductLine,
    loading,
    error
  } = usePinnedFeaturesByProductLine({
    [productLine]: highlightedProducts
  });
  const features = featuresByProductLine[productLine];
  const isAppReady = useGlobalLoading();
  const productLevelOwned = useSelector(state => {
    if (!isAppReady) return null;
    return highestProductLevelFromProductInventorySelector(state, {
      productLine
    });
  });
  const numOfDaysOwned = useSelector(state => {
    if (!productLevelOwned) return 0;
    return daysSinceProductStartDateFromProductInventorySelector(state, {
      productAttributeFilters: {
        productLine,
        productLevel: productLevelOwned
      }
    });
  });
  const showRecommended = productLevelOwned === STARTER && numOfDaysOwned > 30 || shouldShowRecommended;
  const {
    showBoundary
  } = useErrorBoundary();
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError('Error fetching features', {
      error,
      apiNames: highlightedProducts,
      productLine
    }));
  }
  if (!features || !features.length) return null;
  const headerData = buildFeatureTableHeaderData({
    filteredHighlightedProducts: highlightedProducts,
    showRecommended,
    recommendedProductLevel,
    queryParamTerm
  });
  const bodyData = buildFeatureTableBodyData({
    features,
    productLine,
    highlightedProducts,
    screen,
    staticDisplayRowCount: Math.min(((_PinnedFeaturesConfig = PinnedFeaturesConfig[productLine]) === null || _PinnedFeaturesConfig === void 0 ? void 0 : _PinnedFeaturesConfig.length) || 5, 5)
  });
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: headerData,
    bodyData: bodyData,
    canViewNewSeats: isSeatsRedesignUngated,
    onExpand: () => {
      trackFeatureTableInteraction({
        action: 'clicked-expand-table',
        value: getI18nText('upgrades.featureTable.body.expand', {
          locale: 'en-us'
        }),
        productLine,
        productNames: highlightedProducts,
        recommendedPlan: recommendedProductLevel,
        showRecommended
      });
    },
    onCollapse: () => {
      trackFeatureTableInteraction({
        action: 'clicked-collapse-table',
        value: getI18nText('upgrades.featureTable.body.collapse', {
          locale: 'en-us'
        }),
        productLine,
        productNames: highlightedProducts,
        recommendedPlan: recommendedProductLevel,
        showRecommended
      });
    },
    isInbound25PricingPage: isUngatedForInbound25
  });
};
export default withErrorBoundary(ProductFeatureTable, {
  fallback: null,
  errorBoundaryName: 'PRODUCT_FEATURE_TABLE'
});